import { api } from '@/api';
import { PrimitiveArrayField } from '@/components/array-field';
import { Page } from '@/components/dashboard/page';
import { TextField } from '@/components/forms';
import { router } from '@/main';
import { GamePresetSchema, type GamePreset } from '@shared/modelTypes/game-preset-types';
import { createFileRoute } from '@tanstack/react-router';
import { Button, Heading } from 'ht-sdk/react';
import { Form, resolver } from 'ht-sdk/react/forms';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { QR } from '@/components/qr-code';

export const Route = createFileRoute('/dashboard/settings/$id')({
	component: RouteComponent,
	loader: async ({ params }) => {
		const gamePreset = await api.gamePresetGet({ id: params.id });
		return {
			gamePreset: gamePreset.success
				? gamePreset.data
				: {
						id: crypto.randomUUID(),
						connectionCodes: [],
						bonusCodes: [],
					},
		};
	},
});

function RouteComponent() {
	const { gamePreset } = Route.useLoaderData();

	return (
		<Page>
			<GamePresetEditForm gamePreset={gamePreset} />
		</Page>
	);
}

function GamePresetEditForm({ gamePreset }: { gamePreset: GamePreset }) {
	const methods = useForm({
		resolver: resolver(GamePresetSchema),
		defaultValues: gamePreset,
	});

	async function onSubmit(data: GamePreset) {
		console.log('onSubmit', data);

		const res = await api.gamePresetUpdate(data);
		if (!res.success) {
			toast.error(res.message);
			return;
		} else {
			toast.success('Connection codes updated successfully');
			console.log('navigating to /dashboard/settings');
			router.invalidate();
			router.navigate({ to: '/dashboard/settings' });
		}
	}

	return (
		<Form methods={methods} onSubmit={onSubmit}>
			<TextField type="hidden" name="id" label={null} />
			<PrimitiveArrayField<string>
				collapsible={false}
				sortable={false}
				label="Edit Connection Codes"
				name="connectionCodes"
				addLabel="Add connection code"
				add={() => ''}
				build={(_it, _i) => {
					return (
						<div className="flex flex-row gap-2">
							<TextField name={`connectionCodes.${_i}`} label={null} className="uppercase" />
							<div className="max-h-4">
								<QR data={methods.watch(`connectionCodes.${_i}`)} />
							</div>
						</div>
					);
				}}
				buildPreview={(_it, _i) => <Heading mode="secondary">{_it}</Heading>}
			/>
			<PrimitiveArrayField<string>
				collapsible={false}
				sortable={false}
				label="Edit Bonus Codes"
				name="bonusCodes"
				addLabel="Add bonus code"
				add={() => ''}
				build={(_it, _i) => {
					return <TextField name={`bonusCodes.${_i}`} label={null} className="uppercase" />;
				}}
				buildPreview={(_it, _i) => <Heading mode="secondary">{_it}</Heading>}
			/>

			<Button type="submit" className="cursor-pointer">
				Save
			</Button>
		</Form>
	);
}
